import { put, takeLatest, select } from "redux-saga/effects";
import {
  fetchProjectsRequest,
  fetchProjectsSuccess,
  fetchProjectsFailure,
  createProjectRequest,
  createProjectSuccess,
  createProjectFailure,
  fetchProjectDetailRequest,
  fetchProjectDetailSuccess,
  fetchProjectDetailFailure,
  fetchProjectInformationRequest,
  fetchProjectInformationSuccess,
  fetchProjectInformationFailure,
  updateProjectInformationRequest,
  updateProjectInformationSuccess,
  updateProjectInformationFailure,
  shareProjectRequest,
  shareProjectSuccess,
  shareProjectFailure,
  generateShareLinkRequest,
  generateShareLinkSuccess,
  generateShareLinkFailure,
  fetchProjectSharesRequest,
  fetchProjectSharesSuccess,
  fetchProjectSharesFailure,
  updateSharePermissionRequest,
  updateSharePermissionSuccess,
  updateSharePermissionFailure,
  revokeShareRequest,
  revokeShareSuccess,
  revokeShareFailure,
  acceptShareRequest,
  acceptShareSuccess,
  acceptShareFailure,
  fetchUserPermissionRequest,
  fetchUserPermissionSuccess,
  fetchUserPermissionFailure,
  updateProjectRequest,
  updateProjectSuccess,
  updateProjectFailure,
  fetchFrameworksRequest,
  fetchFrameworksSuccess,
  fetchFrameworksFailure,
  fetchProjectRddsRequest,
  fetchProjectRddsSuccess,
  fetchProjectRddsFailure,
  saveProjectRddsRequest,
  saveProjectRddsSuccess,
  saveProjectRddsFailure,
  fetchProjectRieRequest,
  fetchProjectRieSuccess,
  fetchProjectRieFailure,
  saveProjectRieRequest,
  saveProjectRieSuccess,
  saveProjectRieFailure,
  fetchProjectStakeholdersRequest,
  fetchProjectStakeholdersSuccess,
  fetchProjectStakeholdersFailure,
  saveProjectStakeholdersRequest,
  saveProjectStakeholdersSuccess,
  saveProjectStakeholdersFailure,
  updateStakeholderPositionRequest,
  updateStakeholderPositionSuccess,
  updateStakeholderPositionFailure,
  deleteProjectStakeholderRequest,
  deleteProjectStakeholderSuccess,
  deleteProjectStakeholderFailure,
  deleteProjectRieSiteRequest,
  deleteProjectRieSiteSuccess,
  deleteProjectRieSiteFailure,
  deleteProjectRequest,
  deleteProjectSuccess,
  deleteProjectFailure,

} from "./projectsSlice";
import { fastApiURL } from "../../utils/config";
import TokenManager from "../../utils/tokenManager";

const toAbsoluteImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${fastApiURL}${url}`;
};


function* fetchProjectsSaga() {
  try {
    const token = TokenManager.getAccessToken();
    if (!token) {
      // Not authenticated - no projects to show
      yield put(fetchProjectsSuccess([]));
      return;
    }
    const response = yield fetch(`${fastApiURL}/api/projects`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load projects: ${response.status}`);
    }

    const payload = yield response.json();
    const items = (payload.data || []).map((p) => ({
      id: p.id,
      title: p.title,
      description: p.description,
      imageUrl: toAbsoluteImageUrl(p.image_url),
      frameworkId: p.framework_id || null,
      framework: p.framework || null, // ✅ ADD THIS
      created_at: p.created_at,
      updated_at: p.updated_at,
    }));

    yield put(fetchProjectsSuccess(items));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in fetchProjectsSaga:", error);
    yield put(fetchProjectsFailure(error.message));
  }
}

function* createProjectSaga(action) {
  try {
    const { title, description, imageUrl, frameworkId } = action.payload;
    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(`${fastApiURL}/api/projects`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        title,
        description,
        image_url: imageUrl || null,
        framework_id: frameworkId || null,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create project: ${response.status}`);
    }

    const created = yield response.json();

    const item = {
      id: created.id,
      title: created.title,
      description: created.description,
      imageUrl: toAbsoluteImageUrl(created.image_url),
      frameworkId: created.framework_id || null,   // ✅ ADD
      framework: created.framework || null,       // ✅ ADD
      created_at: created.created_at,
      updated_at: created.updated_at,
    };

    yield put(createProjectSuccess(item));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in createProjectSaga:", error);
    yield put(createProjectFailure(error.message));
  }
}

function* fetchProjectDetailSaga(action) {
  try {
    const { projectId } = action.payload;
    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(`${fastApiURL}/api/projects/${projectId}`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorMessage = response.status === 403
        ? "403 - Not allowed"
        : `Failed to load project detail: ${response.status}`;
      throw new Error(errorMessage);
    }

    const payload = yield response.json();
    yield put(fetchProjectDetailSuccess(payload));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in fetchProjectDetailSaga:", error);
    yield put(fetchProjectDetailFailure(error.message));
  }
}

function* fetchProjectInformationSaga(action) {
  try {
    const { projectId } = action.payload;
    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(`${fastApiURL}/api/projects/${projectId}/information`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorMessage = response.status === 403
        ? "403 - Not allowed"
        : `Failed to load project information: ${response.status}`;
      throw new Error(errorMessage);
    }

    const payload = yield response.json();
    yield put(fetchProjectInformationSuccess(payload));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in fetchProjectInformationSaga:", error);
    yield put(fetchProjectInformationFailure(error.message));
  }
}

function* updateProjectInformationSaga(action) {
  try {
    const { projectId, goals, challenges, resources, documents, network, future_scenario_text, strategic_vision } = action.payload;
    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(`${fastApiURL}/api/projects/${projectId}/information`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        goals: goals || [],
        challenges: challenges || [],
        resources: resources || [],
        documents: documents || [],
        network: network || [],
        future_scenario_text: future_scenario_text ?? null,
        strategic_vision: strategic_vision ?? null,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update project information: ${response.status}`);
    }

    const updatedInfo = yield response.json();
    yield put(updateProjectInformationSuccess(updatedInfo));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in updateProjectInformationSaga:", error);
    yield put(updateProjectInformationFailure(error.message));
  }
}

function* shareProjectSaga(action) {
  try {
    const { projectId, email, permission } = action.payload;
    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(`${fastApiURL}/api/projects/${projectId}/share`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        email,
        permission,
      }),
    });

    if (!response.ok) {
      const errorData = yield response.json().catch(() => ({ detail: `Failed to share project: ${response.status}` }));
      throw new Error(errorData.detail || errorData.message || `Failed to share project: ${response.status}`);
    }

    yield put(shareProjectSuccess());
    // Refresh shares list
    yield put(fetchProjectSharesRequest({ projectId }));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in shareProjectSaga:", error);
    yield put(shareProjectFailure(error.message));
  }
}

function* generateShareLinkSaga(action) {
  try {
    const { projectId, permission } = action.payload;
    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(`${fastApiURL}/api/projects/${projectId}/share/link`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        permission,
      }),
    });

    if (!response.ok) {
      const errorData = yield response.json().catch(() => ({ detail: `Failed to generate link: ${response.status}` }));
      throw new Error(errorData.detail || errorData.message || `Failed to generate link: ${response.status}`);
    }

    const payload = yield response.json();
    yield put(generateShareLinkSuccess(payload));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in generateShareLinkSaga:", error);
    yield put(generateShareLinkFailure(error.message));
  }
}

function* fetchProjectSharesSaga(action) {
  try {
    const { projectId } = action.payload;
    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(`${fastApiURL}/api/projects/${projectId}/shares`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load shares: ${response.status}`);
    }

    const payload = yield response.json();
    yield put(fetchProjectSharesSuccess(payload.data || []));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in fetchProjectSharesSaga:", error);
    yield put(fetchProjectSharesFailure(error.message));
  }
}

function* updateSharePermissionSaga(action) {
  try {
    const { projectId, shareId, permission } = action.payload;
    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(`${fastApiURL}/api/projects/${projectId}/shares/${shareId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        permission,
      }),
    });

    if (!response.ok) {
      const errorData = yield response.json().catch(() => ({ detail: `Failed to update permission: ${response.status}` }));
      throw new Error(errorData.detail || errorData.message || `Failed to update permission: ${response.status}`);
    }

    const updatedShare = yield response.json();
    yield put(updateSharePermissionSuccess(updatedShare));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in updateSharePermissionSaga:", error);
    yield put(updateSharePermissionFailure(error.message));
  }
}

function* revokeShareSaga(action) {
  try {
    const { projectId, shareId } = action.payload;
    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(`${fastApiURL}/api/projects/${projectId}/shares/${shareId}`, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to revoke share: ${response.status}`);
    }

    yield put(revokeShareSuccess(shareId));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in revokeShareSaga:", error);
    yield put(revokeShareFailure(error.message));
  }
}

function* acceptShareSaga(action) {
  try {
    const { token } = action.payload;
    const authToken = TokenManager.getAccessToken();
    if (!authToken) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(`${fastApiURL}/api/projects/accept`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        token,
      }),
    });

    if (!response.ok) {
      const errorData = yield response.json().catch(() => ({ detail: `Failed to accept invitation: ${response.status}` }));
      throw new Error(errorData.detail || errorData.message || `Failed to accept invitation: ${response.status}`);
    }

    const payload = yield response.json();
    yield put(acceptShareSuccess());
    return payload; // Return project_id for navigation
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in acceptShareSaga:", error);
    yield put(acceptShareFailure(error.message));
    throw error;
  }
}

function* fetchUserPermissionSaga(action) {
  try {
    const { projectId } = action.payload;
    const token = TokenManager.getAccessToken();
    if (!token) {
      yield put(fetchUserPermissionSuccess(null));
      return;
    }

    const response = yield fetch(`${fastApiURL}/api/projects/${projectId}/permission`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load permission: ${response.status}`);
    }

    const payload = yield response.json();
    yield put(fetchUserPermissionSuccess(payload.permission));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in fetchUserPermissionSaga:", error);
    yield put(fetchUserPermissionFailure(error.message));
  }
}

function* updateProjectSaga(action) {
  try {
    const { projectId, title, description, imageUrl, frameworkId } = action.payload;

    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(`${fastApiURL}/api/projects/${projectId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        title,
        description,
        image_url: imageUrl || null,
        framework_id: frameworkId || null,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update project: ${response.status}`);
    }

    const updated = yield response.json();

    const updatedProject = {
      id: updated.id,
      title: updated.title,
      description: updated.description,
      imageUrl: toAbsoluteImageUrl(updated.image_url),
      frameworkId: updated.framework_id || null,  // ✅ ADD
      framework: updated.framework || null,       // ✅ ADD
      created_at: updated.created_at,
      updated_at: updated.updated_at,
    };

    yield put(updateProjectSuccess(updatedProject));
    yield put(fetchProjectsRequest());
  } catch (error) {
    console.error("Error in updateProjectSaga:", error);
    yield put(updateProjectFailure(error.message));
  }
}

function* fetchFrameworksSaga() {
  try {
    const response = yield fetch(`${fastApiURL}/api/frameworks`); // ✅ better route (if you made separate controller)

    if (!response.ok) {
      throw new Error("Failed to fetch frameworks");
    }

    const data = yield response.json();

    const frameworks = data.map(f => ({
      id: f.id,
      name: f.name,
      description: f.description,
    }));

    yield put(fetchFrameworksSuccess(frameworks));
  } catch (error) {
    yield put(fetchFrameworksFailure(error.message));
  }
}

function* fetchProjectRddsSaga(action) {
  try {
    const { projectId } = action.payload;
    const token = TokenManager.getAccessToken();

    const response = yield fetch(
      `${fastApiURL}/api/projects/${projectId}/rdds`,
      {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch project RDDs");
    }

    const data = yield response.json();
    yield put(fetchProjectRddsSuccess(data));
  } catch (error) {
    yield put(fetchProjectRddsFailure(error.message));
  }
}

function* saveProjectRddsSaga(action) {
  try {
    const { projectId, rdds } = action.payload;
    const token = TokenManager.getAccessToken();

    const response = yield fetch(
      `${fastApiURL}/api/projects/${projectId}/rdds`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          rdds: rdds.map(r => ({
            rdd_id: r.rdd_id,
            description: r.description,
          })),
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to save project RDDs");
    }

    const data = yield response.json();
    yield put(saveProjectRddsSuccess(data));
  } catch (error) {
    yield put(saveProjectRddsFailure(error.message));
  }
}

function* fetchProjectRieSaga(action) {
  try {
    const { projectId } = action.payload;
    const token = TokenManager.getAccessToken();

    const response = yield fetch(
      `${fastApiURL}/api/projects/${projectId}/rie`,
      {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch project RIE");
    }

    const data = yield response.json();
    yield put(fetchProjectRieSuccess(data));
  } catch (error) {
    yield put(fetchProjectRieFailure(error.message));
  }
}

function* saveProjectRieSaga(action) {
  try {
    const { projectId, coordinator, sub_coordinator, sites } = action.payload;
    const token = TokenManager.getAccessToken();

    const response = yield fetch(
      `${fastApiURL}/api/projects/${projectId}/rie`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          coordinator,
          sub_coordinator,
          sites,
        }),
      }
    );

    

    if (!response.ok) {
      throw new Error("Failed to save project RIE");
    }

    const data = yield response.json();

    console.log("RIE RESPONSE:", data);
    
    yield put(saveProjectRieSuccess(data));

    // ✅ CRUCIAAL
    yield put(fetchProjectDetailRequest({ projectId }));

  } catch (error) {
    yield put(saveProjectRieFailure(error.message));
  }
}

function* fetchProjectStakeholdersSaga(action) {
  try {
    const { projectId } = action.payload;
    const token = TokenManager.getAccessToken();

    const response = yield fetch(
      `${fastApiURL}/api/projects/${projectId}/stakeholders`,
      {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch project stakeholders");
    }

    const data = yield response.json();
    yield put(fetchProjectStakeholdersSuccess(data));

  } catch (error) {
    yield put(fetchProjectStakeholdersFailure(error.message));
  }
}
function* saveProjectStakeholdersSaga(action) {
  try {
    const { projectId, stakeholders } = action.payload;
    const token = TokenManager.getAccessToken();

    // 🔥 DEBUG 1: wat komt uit je form?
    console.log("RAW stakeholders from UI:", stakeholders);

    const requestBody = stakeholders.map((s) => ({
      id: s.id || null,

      first_name: s.first_name || "",
      last_name: s.last_name || "",

      contact_details: s.contact_details || null,
      brief_description: s.brief_description || null,

      rdd_id: s.rdd_id || null,
      domain_id: s.domain_id || null,

      is_local_task_force_member:
        s.is_local_task_force_member || false,

      local_task_force_role_id:
        s.local_task_force_role_id || null,

      age:
        s.age !== "" &&
          s.age !== null &&
          s.age !== undefined
          ? Number(s.age)
          : null,

      gender: s.gender || null,

      grue: s.grue || null,

      local_community_trainers:
        s.local_community_trainers || null,

      local_community_trainer_role_id:
        s.local_community_trainer_role_id || null,
        
      cross_cutting_priority:
        s.cross_cutting_priority || null,

      interest:
        s.interest !== undefined
          ? Number(s.interest)
          : null,

      influence:
        s.influence !== undefined
          ? Number(s.influence)
          : null,
    }));

    // 🔥 DEBUG 2: wat stuur je naar backend?
    console.log("REQUEST BODY:", requestBody);

    const response = yield fetch(
      `${fastApiURL}/api/projects/${projectId}/stakeholders`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },


        body: JSON.stringify({
          stakeholders: requestBody
        }),
      }
    );

    // 🔥 DEBUG 3: response status
    console.log("RESPONSE STATUS:", response.status);

    if (!response.ok) {
      const errorData = yield response.json().catch(() => null);

      console.error(
        "BACKEND ERROR FULL:",
        JSON.stringify(errorData, null, 2)
      );

      throw new Error(
        errorData?.detail || "Failed to save project stakeholders"
      );
    }

    const data = yield response.json();

    // 🔥 DEBUG 5: wat komt terug van backend
    console.log("RESPONSE DATA:", data);

    yield put(saveProjectStakeholdersSuccess(data));

    // 🔥 refresh
    // yield put(fetchProjectDetailRequest({ projectId }));
    // yield put(fetchProjectStakeholdersRequest({ projectId }));

  } catch (error) {
    console.error("SAVE STAKEHOLDERS ERROR:", error);
    yield put(saveProjectStakeholdersFailure(error.message));
  }
}

function* deleteProjectStakeholderSaga(action) {
  try {
    const { projectId, stakeholderId } = action.payload;
    const token = TokenManager.getAccessToken();

    const response = yield fetch(
      `${fastApiURL}/api/projects/${projectId}/stakeholders/${stakeholderId}`,
      {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete stakeholder");
    }

    yield put(deleteProjectStakeholderSuccess(stakeholderId));

    // yield put(fetchProjectStakeholdersRequest({ projectId }));
    // yield put(fetchProjectDetailRequest({ projectId }));

  } catch (error) {
    yield put(deleteProjectStakeholderFailure(error.message));
  }
}

function* updateStakeholderPositionSaga(action) {
  try {
    const { projectId, stakeholderId, interest, influence } = action.payload;

    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    console.log("📤 PATCH position:", {
      stakeholderId,
      interest,
      influence,
    });

    const response = yield fetch(
      `${fastApiURL}/api/projects/${projectId}/stakeholders/${stakeholderId}/position`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          interest,
          influence,
        }),
      }
    );

    if (!response.ok) {
      const errorData = yield response.json().catch(() => null);

      console.error("❌ PATCH ERROR:", errorData);

      throw new Error(
        errorData?.detail || "Failed to update stakeholder position"
      );
    }

    const data = yield response.json();

    console.log("✅ PATCH SUCCESS:", data);

    yield put(updateStakeholderPositionSuccess(data));

    // ❗ OPTIONAL (only if backend doesn't return updated entity)
    // yield put(fetchProjectStakeholdersRequest({ projectId }));

  } catch (error) {
    console.error("❌ updateStakeholderPositionSaga:", error);
    yield put(updateStakeholderPositionFailure(error.message));
  }
}

function* deleteProjectRieSiteSaga(action) {
  try {
    const { projectId, siteId } = action.payload;
    const token = TokenManager.getAccessToken();

    const response = yield fetch(
      `${fastApiURL}/api/projects/${projectId}/rie/sites/${siteId}`,
      {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete RIE site");
    }

    yield put(deleteProjectRieSiteSuccess(siteId));

    // 🔥 SAME PATTERN AS OTHERS
    // yield put(fetchProjectRieRequest({ projectId }));
    // yield put(fetchProjectDetailRequest({ projectId }));

  } catch (error) {
    yield put(deleteProjectRieSiteFailure(error.message));
  }
}

function* deleteProjectSaga(action) {
  try {
    const { projectId } = action.payload;
    const token = TokenManager.getAccessToken();

    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = yield fetch(
      `${fastApiURL}/api/projects/${projectId}`,
      {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to delete project: ${response.status}`);
    }

    yield put(deleteProjectSuccess(projectId));

  } catch (error) {
    console.error("Error in deleteProjectSaga:", error);
    yield put(deleteProjectFailure(error.message));
  }
}

export function* watchProjectsSaga() {
  yield takeLatest(fetchProjectsRequest, fetchProjectsSaga);
  yield takeLatest(createProjectRequest, createProjectSaga);
  yield takeLatest(fetchProjectDetailRequest, fetchProjectDetailSaga);
  yield takeLatest(fetchProjectInformationRequest, fetchProjectInformationSaga);
  yield takeLatest(updateProjectInformationRequest, updateProjectInformationSaga);
  yield takeLatest(shareProjectRequest, shareProjectSaga);
  yield takeLatest(generateShareLinkRequest, generateShareLinkSaga);
  yield takeLatest(fetchProjectSharesRequest, fetchProjectSharesSaga);
  yield takeLatest(updateSharePermissionRequest, updateSharePermissionSaga);
  yield takeLatest(revokeShareRequest, revokeShareSaga);
  yield takeLatest(acceptShareRequest, acceptShareSaga);
  yield takeLatest(fetchUserPermissionRequest, fetchUserPermissionSaga);
  yield takeLatest(updateProjectRequest, updateProjectSaga);
  yield takeLatest(fetchFrameworksRequest.type, fetchFrameworksSaga);
  yield takeLatest(fetchProjectRddsRequest.type, fetchProjectRddsSaga);
  yield takeLatest(saveProjectRddsRequest.type, saveProjectRddsSaga);
  yield takeLatest(fetchProjectRieRequest.type, fetchProjectRieSaga);
  yield takeLatest(saveProjectRieRequest.type, saveProjectRieSaga);
  yield takeLatest(fetchProjectStakeholdersRequest.type, fetchProjectStakeholdersSaga);
  yield takeLatest(saveProjectStakeholdersRequest.type, saveProjectStakeholdersSaga);
  yield takeLatest(updateStakeholderPositionRequest.type, updateStakeholderPositionSaga);
  yield takeLatest(deleteProjectStakeholderRequest.type, deleteProjectStakeholderSaga);
  yield takeLatest(deleteProjectRieSiteRequest.type, deleteProjectRieSiteSaga);
  yield takeLatest(deleteProjectRequest.type, deleteProjectSaga);
}

